/* eslint-disable prefer-destructuring */
/**
 * Core Language & Domain Utilities
 * ================================
 * Single source of truth for all language/domain detection and manipulation.
 *
 * This module consolidates commonly used language utilities to:
 * 1. Eliminate duplicate code across the codebase
 * 2. Provide a single import point for all language-related functions
 * 3. Handle China domain detection and cross-domain navigation
 * 4. Manage URL path language prefixes
 * 5. Handle Language Persistence (Cookies)
 * 6. Sync Language from URL (Redux)
 * 7. Transform Routes
 */

import {
  languagesForAmberUserWebsite,
  LanguageConfig,
  SUPPORTED_LANG_VALUES,
} from "locales/langConfig/langConfig";
import setCookie from "helpers/setCookie";
import { getCookie } from "helpers/getCookie";
import config from "@Config/index";

// =============================================================================
// Constants & Types
// =============================================================================

export const SUPPORTED_LANGUAGES = SUPPORTED_LANG_VALUES;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Language route prefix pattern for react-router ":lang(en|zh|fr...)" */
export const LANGUAGE_PREFIX_PATTERN = `:lang(${SUPPORTED_LANG_VALUES.join("|")})`;

const LANGUAGE_PREFIX_REGEX = new RegExp(`^\\/(${SUPPORTED_LANG_VALUES.join("|")})\\/`);
const LANGUAGE_ONLY_REGEX = new RegExp(`^\\/(${SUPPORTED_LANG_VALUES.join("|")})$`);
const LANGUAGE_COOKIE_KEY = "user_language";

// =============================================================================
// Domain Detection
// =============================================================================

/**
 * Check if a hostname is a China domain (.cn)
 */
export const isChinaDomain = (hostname: string): boolean =>
  hostname.endsWith("amberstudent.cn") ||
  hostname === "amberstudent.cn" ||
  hostname.endsWith("-china.staging-amber.com");

/**
 * Client-side check for China domain (safe for SSR)
 */
export const isChinaDomainClient = (): boolean => {
  if (typeof window === "undefined") return false;
  return isChinaDomain(window.location.hostname);
};

// =============================================================================
// Language Configuration Lookups
// =============================================================================

export const getLanguageConfig = (languageCode: string): LanguageConfig | undefined =>
  languagesForAmberUserWebsite.find((lang) => lang.code === languageCode);

export const getDomainForLanguage = (languageCode: string): string | null => {
  const language = getLanguageConfig(languageCode);
  if (language && language.domains.length > 0) {
    return `https://${language.domains[0]}`;
  }
  return null;
};

export const getLanguageFromHostname = (hostname: string): string => {
  if (isChinaDomain(hostname)) return "zh";
  const matchedLanguage = languagesForAmberUserWebsite.find((lang) =>
    lang.domains.includes(hostname),
  );
  return matchedLanguage?.code || "en";
};

export const getLanguageFromRequest = (req: any): string => {
  const hostname = req?.hostname || req?.headers?.host || "";
  return getLanguageFromHostname(hostname);
};

// =============================================================================
// Path Manipulation (from languageRouting)
// =============================================================================

export const isSupportedLanguage = (lang: string): lang is SupportedLanguage =>
  SUPPORTED_LANG_VALUES.includes(lang);

export const extractLanguageFromPath = (path: string): SupportedLanguage | null => {
  const match = path.match(new RegExp(`^\\/(${SUPPORTED_LANG_VALUES.join("|")})(\\/|$)`));
  return match ? (match[1] as SupportedLanguage) : "";
};

export const hasLanguagePrefix = (path: string): boolean =>
  LANGUAGE_PREFIX_REGEX.test(path) || LANGUAGE_ONLY_REGEX.test(path);

export const addLanguageToPath = (
  path: string,
  language: string,
  donotAddPrefixSlash = false,
): string => {
  // Never add prefix for English (default) or Chinese (uses domain)
  if (language === "en" || language === "zh") return path;

  if (hasLanguagePrefix(path)) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${donotAddPrefixSlash ? "" : "/"}${language}${normalizedPath}`;
};

export const removeLanguageFromPath = (path: string): string =>
  path.replace(LANGUAGE_PREFIX_REGEX, "/").replace(LANGUAGE_ONLY_REGEX, "/");

/**
 * Convert a route path to support both with and without language prefix.
 * When a new language is added to langConfig, all routes automatically pick it up.
 *
 * @example
 *   withLang("/places/:id") → ["/places/:id", "/:lang(en|zh|fr|es|de|zhtw|zhhk)/places/:id"]
 *   withLang("/places/:id/video/:videoId", "/places/:id/video")
 *     → ["/places/:id/video/:videoId", "/places/:id/video",
 *        "/:lang(...)/places/:id/video/:videoId", "/:lang(...)/places/:id/video"]
 */
export const withLang = (...paths: string[]): string[] => {
  const result: string[] = [];
  for (const p of paths) {
    if (p.includes(":lang")) {
      result.push(p);
    } else {
      const normalized = p.startsWith("/") ? p : `/${p}`;
      result.push(normalized);
    }
  }
  for (const p of paths) {
    if (!p.includes(":lang")) {
      const normalized = p.startsWith("/") ? p : `/${p}`;
      result.push(`/${LANGUAGE_PREFIX_PATTERN}${normalized}`);
    }
  }
  return result;
};

/** @deprecated Use `withLang` instead */
export const makeLanguageRoutes = (path: string): string[] => withLang(path);

// =============================================================================
// URL Building (from languageUrlBuilder)
// =============================================================================

export const isOnCorrectDomain = (currentHostname: string, targetLanguage: string): boolean => {
  const languageConfig = getLanguageConfig(targetLanguage);
  if (!languageConfig) return false;
  return languageConfig.domains.some((domain) => currentHostname.includes(domain));
};

export const needsCrossDomainNavigation = (
  currentLanguage: string,
  targetLanguage: string,
): boolean => {
  if (currentLanguage === "zh" && targetLanguage !== "zh") return true;
  if (currentLanguage !== "zh" && targetLanguage === "zh") return true;

  if (typeof window !== "undefined") {
    return !isOnCorrectDomain(window.location.hostname, targetLanguage);
  }
  return false;
};

interface BuildLanguageUrlOptions {
  path: string;
  targetLanguage: string;
  currentLanguage?: string;
  preserveSearch?: boolean;
  isExternal?: boolean;
}

interface LanguageUrlResult {
  url: string;
  needsCrossDomainNavigation: boolean;
  targetDomain: string | null;
}

export const buildLanguageUrl = (options: BuildLanguageUrlOptions): LanguageUrlResult => {
  const {
    path,
    targetLanguage,
    currentLanguage = "en",
    preserveSearch = true,
    isExternal = false,
  } = options;

  if (isExternal || path.startsWith("http://") || path.startsWith("https://")) {
    return { url: path, needsCrossDomainNavigation: false, targetDomain: null };
  }

  const requiresCrossDomain = needsCrossDomainNavigation(currentLanguage, targetLanguage);
  const targetDomain = getDomainForLanguage(targetLanguage);

  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  let searchParams = "";
  if (preserveSearch && typeof window !== "undefined") {
    searchParams = window.location.search;
  }

  // Handle Chinese (no prefix, .cn domain)
  if (targetLanguage === "zh") {
    normalizedPath = removeLanguageFromPath(normalizedPath);
    if (requiresCrossDomain && targetDomain) {
      return {
        url: `${targetDomain}${normalizedPath}${searchParams}`,
        needsCrossDomainNavigation: true,
        targetDomain,
      };
    }
    return {
      url: `${normalizedPath}${searchParams}`,
      needsCrossDomainNavigation: false,
      targetDomain: null,
    };
  }

  // Handle Non-Chinese
  normalizedPath = removeLanguageFromPath(normalizedPath);
  if (targetLanguage !== "en") {
    normalizedPath = addLanguageToPath(normalizedPath, targetLanguage);
  }

  if (currentLanguage === "zh" && requiresCrossDomain) {
    const englishDomain = getDomainForLanguage("en");
    if (englishDomain) {
      return {
        url: `${englishDomain}${normalizedPath}${searchParams}`,
        needsCrossDomainNavigation: true,
        targetDomain,
      };
    }
  }

  return {
    url: `${normalizedPath}${searchParams}`,
    needsCrossDomainNavigation: false,
    targetDomain: null,
  };
};

export const buildCurrentLanguageUrl = (
  path: string,
  currentLanguage: string,
  isExternal = false,
): string =>
  buildLanguageUrl({
    path,
    targetLanguage: currentLanguage,
    currentLanguage,
    isExternal,
  }).url;

export const shouldAddLanguagePrefix = (language: string): boolean => {
  if (language === "zh") return false;
  if (language === "en") return false;
  return true;
};

// =============================================================================
// Cross-Domain Switching (from customDomainSwitch)
// =============================================================================

export const customDomainSwitch = ({ url }: { url: string }): void => {
  if (!url) return;
  window.location.href = url;
};

// =============================================================================
// Persistence (Merged from languagePersistence.ts)
// =============================================================================

/**
 * Utility for persisting user language preference
 *
 * NOTE: We use Cookies ONLY (not LocalStorage) because the server (SSR)
 * needs to read this preference to render the correct language.
 */

export const saveLanguagePreference = (language: string): void => {
  if (typeof window === "undefined") return;

  // Determine the correct domain
  let domain = "";
  const hostname = window.location.hostname;

  if (hostname.includes("amberstudent.cn")) {
    // China domain
    domain = ".amberstudent.cn";
  } else if (hostname.includes("amberstudent.com")) {
    // Main production domain
    domain = ".amberstudent.com";
  } else if (hostname.includes("staging-amber.com")) {
    // Staging domain
    domain = ".staging-amber.com";
  }
  // For localhost and other domains, leave domain empty (defaults to current host)

  setCookie({
    cname: LANGUAGE_COOKIE_KEY,
    cvalue: language,
    exdays: 365,
    secure: !__DEV__,
    domain,
  });

  // Debug log to verify cookie is saved (only in development)
  if (__DEV__) {
    console.log(`[Language] Saved language preference: ${language}`);
    console.log(`[Language] Cookie domain: ${domain || "current host"}`);
    console.log(`[Language] Cookie value:`, getCookie(LANGUAGE_COOKIE_KEY));
  }
};

export const getLanguagePreference = (): string | null => {
  const cookieVal = getCookie(LANGUAGE_COOKIE_KEY);
  return cookieVal || null;
};

export const getLanguageFromCookie = (): string | null => {
  const cookieVal = getCookie(LANGUAGE_COOKIE_KEY);
  return cookieVal || null;
};

export const clearLanguagePreference = (): void => {
  if (typeof window === "undefined") return;

  // Determine the correct domain
  let domain = "";
  const hostname = window.location.hostname;

  if (hostname.includes("amberstudent.cn")) {
    domain = ".amberstudent.cn";
  } else if (hostname.includes("amberstudent.com")) {
    domain = ".amberstudent.com";
  } else if (hostname.includes("staging-amber.com")) {
    domain = ".staging-amber.com";
  }

  setCookie({
    cname: LANGUAGE_COOKIE_KEY,
    cvalue: "",
    exdays: -1,
    secure: !__DEV__,
    domain,
  });
};

export const hasLanguagePreference = (): boolean => getLanguagePreference() !== null;

// =============================================================================
// Sync Logic (Moved to languageReduxHelpers.ts to avoid circular dependency)
// =============================================================================

/**
 * NOTE: Redux-dependent sync functions have been moved to:
 * src/utils/languageReduxHelpers.ts
 *
 * This was necessary to break the circular dependency:
 * store/index.ts → languageRouterMiddleware → languageCore → store/index.ts
 *
 * Functions moved:
 * - syncLanguageFromUrl
 * - syncLanguageFromUrlClient
 * - languageFromUrlMiddleware
 */

/**
 * Get user language for SSR (Server-Side Rendering)
 * Used by China domain to determine initial language
 *
 * Priority:
 * 1. Language from URL path (e.g., /fr/search)
 * 2. Language from query param (e.g., ?lang=zh)
 * 3. Language from domain (e.g., amberstudent.cn = zh)
 */
export const getUserLanguageSSR = (req: any): string => {
  const pathname = req.path || req.url || "";
  const requestedLang = extractLanguageFromPath(pathname) || req.query?.lang;

  if (requestedLang) {
    if (SUPPORTED_LANGUAGES.includes(requestedLang)) {
      return requestedLang;
    }
  }

  // Check domain for language
  const hostDomain = req.host || "";
  const englishDomains = languagesForAmberUserWebsite.find(
    (lang) => lang.name === "english",
  )?.domains;

  if (englishDomains?.includes(hostDomain)) {
    return "en";
  }

  if (config.IS_CHINA_APP) return "zh";

  // Default to Chinese for China domain / unknown domains
  return "en";
};

// =============================================================================
// Route Transformation (Merged from transformRoutesWithLanguage.ts)
// =============================================================================

/**
 * Transform a single route to support language prefixes
 * Creates duplicate routes - one with language prefix and one without
 */
const transformRoute = (route: any): any[] => {
  // If no path or it's a redirect, return as is
  if (!route.path || route.redirect) {
    return [route];
  }

  // If path already has language pattern, return as is
  if (route.path.includes(":lang")) {
    return [route];
  }

  const basePath = route.path;
  const languagePrefixedPath = `/${LANGUAGE_PREFIX_PATTERN}${basePath}`;

  // Return two routes: one with language prefix (higher priority) and one without
  return [
    {
      ...route,
      path: languagePrefixedPath,
    },
    route, // Original route without language prefix
  ];
};

/**
 * Transform route configuration to support language prefixes
 * Recursively processes nested routes
 */
export const transformRoutesWithLanguage = (routes: any[]): any[] => {
  const transformedRoutes: any[] = [];

  routes.forEach((route) => {
    // Transform nested routes if they exist
    if (route.routes && Array.isArray(route.routes)) {
      const expandedRoutes: any[] = [];

      route.routes.forEach((nestedRoute: any) => {
        const transformed = transformRoute(nestedRoute);
        expandedRoutes.push(...transformed);
      });

      transformedRoutes.push({
        ...route,
        routes: expandedRoutes,
      });
    } else {
      // Transform single route
      const transformed = transformRoute(route);
      transformedRoutes.push(...transformed);
    }
  });

  return transformedRoutes;
};

export default transformRoutesWithLanguage;
